import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { articleAPI } from '../../services/api';

export const fetchArticles = createAsyncThunk('article/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await articleAPI.getAll(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch articles');
  }
});

export const fetchArticleById = createAsyncThunk('article/fetchById', async (id, { rejectWithValue }) => {
  try {
    const response = await articleAPI.getById(id);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch article');
  }
});

export const createArticle = createAsyncThunk('article/create', async (data, { rejectWithValue }) => {
  try {
    const response = await articleAPI.create(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create article');
  }
});

export const updateArticle = createAsyncThunk('article/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await articleAPI.update(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update article');
  }
});

export const deleteArticle = createAsyncThunk('article/delete', async (id, { rejectWithValue }) => {
  try {
    await articleAPI.delete(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete article');
  }
});

export const bulkDeleteArticles = createAsyncThunk('article/bulkDelete', async (ids, { rejectWithValue }) => {
  try {
    await articleAPI.bulkAction({ action: 'delete', articleIds: ids });
    return ids;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete articles');
  }
});

const articleSlice = createSlice({
  name: 'article',
  initialState: {
    articles: [],
    currentArticle: null,
    loading: false,
    error: null,
    pagination: null,
  },
  reducers: {
    clearCurrentArticle: (state) => {
      state.currentArticle = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticles.pending, (state) => { state.loading = true; })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.loading = false;
        state.articles = action.payload.data || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchArticleById.pending, (state) => { state.loading = true; })
      .addCase(fetchArticleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentArticle = action.payload;
      })
      .addCase(fetchArticleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.articles.unshift(action.payload);
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        const index = state.articles.findIndex(a => a.id === action.payload.id);
        if (index !== -1) state.articles[index] = action.payload;
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.articles = state.articles.filter(a => a.id !== action.payload);
      })
      .addCase(bulkDeleteArticles.fulfilled, (state, action) => {
        state.articles = state.articles.filter(a => !action.payload.includes(a.id));
      });
  },
});

export const { clearCurrentArticle, clearError } = articleSlice.actions;
export default articleSlice.reducer;
